import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import UserDataService from "./services/UserService";
import DataTable from "../components/DataTable";
import { useSelector } from "react-redux";
import {
  Box,
  Container,
  Typography,
  Button,
  Chip
} from "@mui/material";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";

const Users = (props) => {
  const navigate = useNavigate();
  const { user: currentUser } = useSelector((state) => state.auth || {});

  // fallback to localStorage so page doesn't immediately redirect before redux hydrates
  const storedUser = useMemo(() => {
    if (currentUser) return currentUser;
    try {
      const s = localStorage.getItem("user") || localStorage.getItem("profile");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  }, [currentUser]);
  // detect president role: used to hide edit action
  const isPresident = useMemo(() => {
    const viewAsRole = sessionStorage.getItem("mtc_view_as_role");
    if (viewAsRole) return viewAsRole.toLowerCase().includes("president") || viewAsRole.toLowerCase().includes("financial");
    const roles = storedUser?.roles ?? storedUser?.role ?? storedUser?.rolesList ?? null;
    if (!roles) return false;
    if (typeof roles === "string") return roles.toLowerCase().includes("president") || roles.toLowerCase().includes("financial");
    if (Array.isArray(roles)) return roles.some(r => {
      const rs = String(r).toLowerCase();
      return rs.includes("president") || rs.includes("financial");
    });
    return false;
  }, [storedUser]);

  const isRegistrarOrAdmin = useMemo(() => {
    const viewAsRole = sessionStorage.getItem("mtc_view_as_role");
    if (viewAsRole) return viewAsRole.toLowerCase().includes("admin") || viewAsRole.toLowerCase().includes("registrar") || viewAsRole.toLowerCase().includes("secretary");
    const roles = storedUser?.roles ?? storedUser?.role ?? storedUser?.rolesList ?? null;
    if (!roles) return false;
    if (typeof roles === "string") return roles.toLowerCase().includes("admin") || roles.toLowerCase().includes("registrar") || roles.toLowerCase().includes("secretary");
    if (Array.isArray(roles)) return roles.some(r => {
      const rs = String(r).toLowerCase();
      return rs.includes("admin") || rs.includes("registrar") || rs.includes("secretary");
    });
    return false;
  }, [storedUser]);

  useEffect(() => {
    // require authenticated user only
    if (!storedUser) {
      navigate("/login");
    }
  }, [storedUser, navigate]);

  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("Active");

  const getMemberStatus = (u) => {
    const raw = (u.approvedOrNotApproved || "Active").toString().trim().toLowerCase();
    if (raw === "withdraw" || raw === "withdrawn" || raw === "withdrawal") return "Withdraw";
    if (raw === "expel" || raw === "expelled") return "Expel";
    if (raw === "decease" || raw === "deceased") return "Decease";
    if (raw === "non-active" || raw === "inactive" || raw === "non active") return "Non-Active";
    return "Active";
  };

  const statusCounts = useMemo(() => {
    const counts = { Active: 0, "Non-Active": 0, Withdraw: 0, Expel: 0, Decease: 0, All: users.length };
    users.forEach((u) => {
      const st = getMemberStatus(u);
      if (counts[st] !== undefined) counts[st]++;
      else counts.Active++;
    });
    return counts;
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (statusFilter === "All") return users;
    return users.filter((u) => getMemberStatus(u) === statusFilter);
  }, [users, statusFilter]);

  useEffect(() => {
    retrieveUsers();
  }, []);

  const retrieveUsers = () => {
    UserDataService.getAll()
      .then((response) => {
        const data = response.data || [];
        setUsers(data);
      })
      .catch((e) => {
        // silent fail
      });
  };

  const columns = useMemo(
    () => [
      {
        id: "registrationNo",
        Header: "Reg. No",
        accessor: (row) =>
          row.registrationNo ||
          row.registration_number ||
          row.regNo ||
          row.registration ||
          row.membershipId ||
          row.membership_id ||
          row.id ||
          "—",
      },
      {
        id: "username",
        Header: "Username",
        accessor: (row) => row.username || row.userName || row.email || "—",
      },
      {
        id: "firstName",
        Header: "First Name",
        accessor: (row) =>
          row.firstName ||
          row.first_name ||
          row.firstname ||
          (row.name ? row.name.split(" ")[0] : "") ||
          "—",
      },
      {
        id: "lastName",
        Header: "Last Name",
        accessor: (row) =>
          row.lastName ||
          row.last_name ||
          row.lastname ||
          (row.name
            ? (() => {
                const p = row.name.split(" ");
                return p.length > 1 ? p.slice(-1)[0] : "";
              })()
            : "") ||
          "—",
      },
      {
        id: "phoneNumber",
        Header: "Phone Number",
        accessor: (row) =>
          row.phoneNumber || row.phone || row.contact || row.phone_number || "—",
      },
      {
        id: "status",
        Header: "Status",
        accessor: (row) => getMemberStatus(row),
        Cell: ({ row }) => {
          const st = getMemberStatus(row);
          const bg =
            st === "Active" ? "#059669" :
            st === "Non-Active" ? "#D97706" :
            st === "Withdraw" ? "#854D0E" :
            st === "Expel" ? "#DC2626" :
            st === "Decease" ? "#475569" : "#059669";
          return (
            <Chip
              label={st}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.72rem",
                fontWeight: 700,
                backgroundColor: bg,
                color: "#FFFFFF",
              }}
            />
          );
        },
      },
      {
        id: "actions",
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => {
          const id = row._id || row.id;
          return (
            <Box sx={{ display: "flex", gap: 1 }}>
              {isPresident ? (
                <Button
                  component={Link}
                  to={`/users/${id}`}
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.3, px: 1.5, borderRadius: 1.5, fontWeight: 600 }}
                >
                  View
                </Button>
              ) : (
                <Button
                  component={Link}
                  to={`/users/${id}`}
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.3, px: 1.5, borderRadius: 1.5, fontWeight: 600 }}
                >
                  Edit
                </Button>
              )}
            </Box>
          );
        },
      },
    ],
    [isPresident]
  );

  const dtColumns = useMemo(
    () =>
      columns.map((c) => ({
        id: c.id || c.Header,
        Header: c.Header,
        accessor: c.accessor,
        Cell: c.Cell,
      })),
    [columns]
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 5 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#0b3b60", letterSpacing: "-0.02em" }}>
            Member Directory
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View, manage, and inspect cooperative member records and accounts.
          </Typography>
        </Box>
        {isRegistrarOrAdmin && (
          <Button
            component={Link}
            to="/signup"
            variant="contained"
            color="primary"
            startIcon={<PersonAddAltIcon />}
            sx={{
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
              px: 2.5,
              py: 1,
              boxShadow: "0 4px 14px rgba(11, 59, 96, 0.25)",
            }}
          >
            Register New Member
          </Button>
        )}
      </Box>

      {/* Status Filter Buttons */}
      <Box sx={{ display: "flex", gap: 1, mb: 2.5, flexWrap: "wrap", alignItems: "center" }}>
        <Typography variant="caption" sx={{ color: "#334155", fontWeight: 700, mr: 0.5 }}>
          Status:
        </Typography>
        {[
          { key: "Active", label: `Active (${statusCounts.Active || 0})`, color: "#059669" },
          { key: "Non-Active", label: `Non-Active (${statusCounts["Non-Active"] || 0})`, color: "#D97706" },
          { key: "Withdraw", label: `Withdraw (${statusCounts.Withdraw || 0})`, color: "#854D0E" },
          { key: "Expel", label: `Expel (${statusCounts.Expel || 0})`, color: "#DC2626" },
          { key: "Decease", label: `Decease (${statusCounts.Decease || 0})`, color: "#475569" },
          { key: "All", label: `All (${statusCounts.All || 0})`, color: "#0B3B60" }
        ].map(({ key, label, color }) => (
          <Button
            key={key}
            size="small"
            variant={statusFilter === key ? "contained" : "outlined"}
            onClick={() => setStatusFilter(key)}
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              py: 0.35,
              px: 1.2,
              fontSize: "0.75rem",
              fontWeight: 700,
              backgroundColor: statusFilter === key ? color : "#ffffff",
              borderColor: statusFilter === key ? "transparent" : "#CBD5E1",
              color: statusFilter === key ? "#ffffff" : "#334155",
              "&:hover": {
                backgroundColor: statusFilter === key ? color : "#F8FAFC",
              }
            }}
          >
            {label}
          </Button>
        ))}
      </Box>

      {/* Main Data Table */}
      <DataTable
        columns={dtColumns}
        rows={filteredUsers}
        rowKey={(r) => r._id || r.id}
        initialPageSize={10}
        onRowClick={(row) => navigate(`/users/${row._id || row.id}`)}
        placeholder="Search username, lastname, phone, reg no..."
      />
    </Container>
  );
};

export default Users;
